import React from "react";
import PropTypes from "prop-types";

const CateringOrderDetailsModal = ({ 
  isOpen, 
  onClose, 
  orderDetails 
}) => {
  console.log("Modal Props - isOpen:", isOpen, "orderDetails:", orderDetails);
  console.log("Phone Number:", orderDetails?.customerPhoneNumber);
  console.log("Phone Country Code:", orderDetails?.customerPhoneCountryCode);
  console.log("Delivery Area:", orderDetails?.deliveryArea);
  console.log("Is Combined Order:", orderDetails?.isCombineOrder);
  console.log("Combine Orders:", orderDetails?.combineOrders);
  
  if (!isOpen || !orderDetails) return null;

  // Handle print functionality
  const handlePrint = () => {
    // Use a timeout to ensure styles are applied
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Check if this is a combined order
  const isCombinedOrder = orderDetails.isCombineOrder === "yes" || orderDetails.isCombineOrder === true;
  
  // Calculate total add-ons price (exclude complimentary items)
  let totalAddOnsPrice = 0;
  
  if (isCombinedOrder && orderDetails.combineOrders && orderDetails.combineOrders.length > 0) {
    // For combined orders, sum add-ons from all packages
    totalAddOnsPrice = orderDetails.combineOrders.reduce((total, pkg) => {
      const packageAddOns = pkg.service_item?.reduce((pkgTotal, item) => {
        const isComplimentary = item.is_complementary === true || item.is_complementary === 1;
        if (isComplimentary) {
          return pkgTotal;
        }
        const addOnPrice = parseFloat(item.add_on_price || 0);
        return pkgTotal + addOnPrice;
      }, 0) || 0;
      return total + packageAddOns;
    }, 0);
  } else {
    // For single orders, use serviceItems
    totalAddOnsPrice = orderDetails.serviceItems?.reduce((total, item) => {
      const isComplimentary = item.is_complementary === true || item.is_complementary === 1;
      if (isComplimentary) {
        return total;
      }
      const addOnPrice = parseFloat(item.add_on_price || item.menu_item?.add_on_price || 0);
      return total + addOnPrice;
    }, 0) || 0;
  }

  // Delivery charges
  const deliveryCharges = parseFloat(orderDetails.deliveryCharges || 0);
  
  // Discount price
  const discountPrice = parseFloat(orderDetails.discount_price || 0);
  
  // Payment processing fee
  const paymentFee = parseFloat(orderDetails.paymentProcessingFee || 0);
  
  // Calculate total price - handle empty string case for Bilbayt orders
  // For combined orders, sum up package prices if order_total_price is empty
  let totalPrice = parseFloat(orderDetails.order_total_price || orderDetails.total_price || 0);
  
  // If total is 0 or NaN (empty string), calculate from combined orders or Bilbayt
  if (!totalPrice || isNaN(totalPrice)) {
    if (isCombinedOrder && orderDetails.combineOrders && orderDetails.combineOrders.length > 0) {
      // Sum up all package prices for combined orders
      totalPrice = orderDetails.combineOrders.reduce((sum, pkg) => {
        return sum + parseFloat(pkg.service_item_price || 0);
      }, 0);
    } else {
      // For single orders, use service_item_price
      totalPrice = parseFloat(orderDetails.service_item_price || 0);
    }
    
    // Add add-ons, delivery charges and payment fee
    totalPrice = totalPrice + totalAddOnsPrice + deliveryCharges + paymentFee;
  }
  
  // Calculate base price by subtracting add-ons, delivery, and fees from total
  // Base Price = Total - Add-ons - Delivery - Payment Fee
  const basePrice = totalPrice - totalAddOnsPrice - deliveryCharges - paymentFee;

  return (
    <>
      <style>{`
        @media print {
          * {
            visibility: hidden;
          }
          .catering-order-modal,
          .catering-order-modal * {
            visibility: visible;
          }
          .catering-order-modal {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
            padding: 0;
            margin: 0;
          }
          .catering-order-modal > div {
            max-height: none !important;
            overflow: visible !important;
            display: block !important;
          }
          .print-content {
            overflow: visible !important;
            max-height: none !important;
          }
          .combined-package-item {
            page-break-inside: avoid;
          }
          .combined-packages-section {
            page-break-before: always;
          }
          @page {
            margin: 1.5cm;
            size: A4;
          }
        }
      `}</style>
      <div className="catering-order-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:block print:bg-white print:static print:p-0">
        <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col print:max-w-full print:max-h-none print:shadow-none print:overflow-visible print:block">
          {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 text-white p-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-2xl font-bold">Order Details</h2>
                {isCombinedOrder && (
                  <span className="px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                    COMBINED ORDER
                  </span>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-slate-100">Order #{orderDetails.orderNo}</p>
                {!isCombinedOrder && (
                  <>
                    <p className="text-slate-200">{orderDetails.restaurant}</p>
                    <p className="text-slate-300">{orderDetails.serviceItemName}</p>
                  </>
                )}
                {isCombinedOrder && (
                  <p className="text-slate-200">{orderDetails.combineOrders?.length || 0} packages combined</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 hover:bg-white/20 rounded-full transition-colors print:hidden"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="print-content flex-1 overflow-y-auto p-6 print:overflow-visible">
          {/* Customer & Event Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2 tracking-wide">Customer Information</h3>
              {orderDetails.customerName && (
                <p className="text-gray-700 mb-1">
                  <span className="font-medium text-slate-600">Name:</span> {orderDetails.customerName}
                </p>
              )}
              {orderDetails.customerPhoneNumber && (
                <p className="text-gray-700 mb-1">
                  <span className="font-medium text-slate-600">Phone:</span> {orderDetails.customerPhoneCountryCode || ''} {orderDetails.customerPhoneNumber}
                </p>
              )}
              {orderDetails.deliveryArea && (
                <p className="text-gray-700 mb-1">
                  <span className="font-medium text-slate-600">Area:</span> {orderDetails.deliveryArea}
                </p>
              )}
              <p className="text-gray-700">
                <span className="font-medium text-slate-600">Order Placed:</span> {orderDetails.orderDate || "N/A"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2 tracking-wide">Event Details</h3>
              {!isCombinedOrder ? (
                <>
                  <p className="text-gray-700 mb-1">
                    <span className="font-medium text-slate-600">Date:</span> {orderDetails.serviceItemDate || "N/A"}
                  </p>
                  <p className="text-gray-700 mb-1">
                    <span className="font-medium text-slate-600">Time:</span> {orderDetails.serviceItemSlot || "N/A"}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium text-slate-600">PAX:</span> {orderDetails.noOfPax}
                  </p>
                </>
              ) : (
                <p className="text-gray-700 text-sm">
                  Event details vary per package. See individual packages below.
                </p>
              )}
            </div>
          </div>

          {/* Delivery Address Section */}
          {orderDetails.addressDetails && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-3 tracking-wide">Delivery Address</h3>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                {orderDetails.userType === "customer" ? (
                  // Customer Address Format
                  <>
                    {orderDetails.addressDetails.addressType && (
                      <p className="text-gray-700 mb-2">
                        <span className="inline-block px-2 py-1 bg-slate-200 text-slate-700 text-xs font-semibold rounded uppercase">
                          {orderDetails.addressDetails.addressType}
                        </span>
                      </p>
                    )}
                    
                    {/* For House Type */}
                    {orderDetails.addressDetails.addressType?.toLowerCase() === "house" && (
                      <>
                        {orderDetails.addressDetails.houseNo && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">House No:</span> {orderDetails.addressDetails.houseNo}
                          </p>
                        )}
                        {orderDetails.addressDetails.street && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Street:</span> {orderDetails.addressDetails.street}
                          </p>
                        )}
                        {orderDetails.addressDetails.block && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Block:</span> {orderDetails.addressDetails.block}
                          </p>
                        )}
                        {orderDetails.addressDetails.avenue && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Avenue:</span> {orderDetails.addressDetails.avenue}
                          </p>
                        )}
                      </>
                    )}

                    {/* For Apartment Type */}
                    {orderDetails.addressDetails.addressType?.toLowerCase() === "apartment" && (
                      <>
                        {orderDetails.addressDetails.building && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Building:</span> {orderDetails.addressDetails.building}
                          </p>
                        )}
                        {orderDetails.addressDetails.floor && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Floor:</span> {orderDetails.addressDetails.floor}
                          </p>
                        )}
                        {orderDetails.addressDetails.apartmentNo && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Apartment No:</span> {orderDetails.addressDetails.apartmentNo}
                          </p>
                        )}
                        {orderDetails.addressDetails.street && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Street:</span> {orderDetails.addressDetails.street}
                          </p>
                        )}
                        {orderDetails.addressDetails.block && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Block:</span> {orderDetails.addressDetails.block}
                          </p>
                        )}
                      </>
                    )}

                    {/* For Office Type */}
                    {orderDetails.addressDetails.addressType?.toLowerCase() === "office" && (
                      <>
                        {orderDetails.addressDetails.companyName && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Company:</span> {orderDetails.addressDetails.companyName}
                          </p>
                        )}
                        {orderDetails.addressDetails.building && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Building:</span> {orderDetails.addressDetails.building}
                          </p>
                        )}
                        {orderDetails.addressDetails.floor && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Floor:</span> {orderDetails.addressDetails.floor}
                          </p>
                        )}
                        {orderDetails.addressDetails.street && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Street:</span> {orderDetails.addressDetails.street}
                          </p>
                        )}
                        {orderDetails.addressDetails.block && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Block:</span> {orderDetails.addressDetails.block}
                          </p>
                        )}
                      </>
                    )}

                    {/* Contact Number */}
                    {orderDetails.addressDetails.mobileNumber && (
                      <p className="text-gray-700 mt-2 pt-2 border-t border-slate-300">
                        <span className="font-medium text-slate-600">Contact:</span> {orderDetails.addressDetails.countryCode} {orderDetails.addressDetails.mobileNumber}
                      </p>
                    )}
                  </>
                ) : (
                  // Company Address Format
                  <>
                    {orderDetails.addressDetails.address1 && (
                      <p className="text-gray-700 mb-1">{orderDetails.addressDetails.address1}</p>
                    )}
                    {orderDetails.addressDetails.address2 && (
                      <p className="text-gray-700">{orderDetails.addressDetails.address2}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Order Status & Payment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2">Status</h3>
              <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-semibold shadow-sm ${
                orderDetails.orderStatus === "Submitted" ? "bg-yellow-500 text-white" :
                orderDetails.orderStatus === "Accepted" ? "bg-blue-500 text-white" :
                orderDetails.orderStatus === "Canceled" ? "bg-red-500 text-white" :
                orderDetails.orderStatus === "Paid" ? "bg-green-500 text-white" :
                orderDetails.orderStatus === "Pending Payment" ? "bg-orange-500 text-white" :
                orderDetails.orderStatus === "Delivered" ? "bg-teal-500 text-white" :
                orderDetails.orderStatus === "Preparing" ? "bg-indigo-500 text-white" :
                orderDetails.orderStatus === "On the way" ? "bg-purple-500 text-white" :
                orderDetails.orderStatus === "Ready for Dispatch" ? "bg-amber-500 text-white" :
                "bg-slate-500 text-white"
              }`}>
                {orderDetails.orderStatus}
              </span>
            </div>
            {orderDetails.paymentMethod && (
              <div>
                <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2">Payment Method</h3>
                <p className="text-gray-700 font-medium">{orderDetails.paymentMethod}</p>
              </div>
            )}
          </div>

          {/* Modification Info */}
          {orderDetails.updatedByAgentId && String(orderDetails.updatedByAgentId).trim() !== "" && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="bg-slate-50 border border-slate-300 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  <h3 className="text-sm font-semibold text-slate-800 uppercase">Order Modified</h3>
                </div>
                <p className="text-sm text-slate-700">
                  <span className="font-medium">Modified by Agent #{orderDetails.updatedByAgentId}</span>
                  {orderDetails.updatedDatetime && (
                    <span className="ml-2 text-slate-600">on {orderDetails.updatedDatetime}</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Combined Order Packages */}
          {isCombinedOrder && orderDetails.combineOrders && orderDetails.combineOrders.length > 0 && (
            <div className="combined-packages-section mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Combined Packages</h3>
              <div className="space-y-4">
                {orderDetails.combineOrders.map((pkg, index) => {
                  // Calculate add-ons for this package (exclude complimentary items)
                  const packageAddOnsPrice = pkg.service_item?.reduce((total, item) => {
                    // Skip items that are complimentary - they are part of the package, not add-ons
                    const isComplimentary = item.is_complementary === true || item.is_complementary === 1;
                    if (isComplimentary) {
                      return total;
                    }
                    const addOnPrice = parseFloat(item.add_on_price || 0);
                    return total + addOnPrice;
                  }, 0) || 0;

                  return (
                    <div key={index} className="combined-package-item bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-5 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-bold rounded">
                              Package {index + 1}
                            </span>
                            <h4 className="font-bold text-slate-800 text-base">
                              {pkg.service_item_name_en || pkg.service_item_name_ar}
                            </h4>
                          </div>
                          <p className="text-sm text-slate-600 font-medium">
                            {pkg.restaurant_name_en || pkg.restaurant_name_ar}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-purple-700">
                            {orderDetails.currency} {parseFloat(pkg.service_item_price || 0).toFixed(3)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-purple-200">
                        <div>
                          <p className="text-xs text-slate-500 font-semibold uppercase">PAX</p>
                          <p className="text-sm text-slate-700 font-medium">{pkg.no_of_pax}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-semibold uppercase">Date</p>
                          <p className="text-sm text-slate-700 font-medium">{pkg.service_item_date}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-slate-500 font-semibold uppercase">Time Slot</p>
                          <p className="text-sm text-slate-700 font-medium">{pkg.service_item_slot}</p>
                        </div>
                      </div>
                      
                      {/* Package Items */}
                      {pkg.service_item && pkg.service_item.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <p className="text-xs text-slate-500 font-semibold uppercase mb-2">Package Items</p>
                          <div className="space-y-2">
                            {pkg.service_item.map((item, itemIndex) => {
                              const itemAddOnPrice = parseFloat(item.add_on_price || 0);
                              const quantity = parseInt(item.quantity || item.choose || 1);
                              const isComplimentary = item.is_complementary === true || item.is_complementary === 1;
                              
                              return (
                                <div key={itemIndex} className="flex justify-between items-start text-xs bg-white/50 p-2 rounded">
                                  <div className="flex-1">
                                    <p className="font-medium text-slate-800">
                                      {item.menu_item?.name_en || item.menu_item?.name_ar || "Item"}
                                      {item.add_on_type && item.add_on_type.trim() !== "" && (
                                        <span className="ml-1 text-xs font-medium text-white bg-amber-100 px-1.5 py-0.5 rounded capitalize">
                                          {item.add_on_type}
                                        </span>
                                      )}
                                      {isComplimentary && (
                                        <span className="ml-1 text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                                          Complimentary
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-slate-500 text-xs">
                                      {item.category_name_en || item.category_name_ar} • Qty: {quantity}
                                      {item.portion && ` • Portion: ${item.portion}`}
                                    </p>
                                  </div>
                                  <div className="text-right ml-2">
                                    {/* Show add-on price only if NOT complimentary and price > 0 */}
                                    {!isComplimentary && itemAddOnPrice > 0 ? (
                                      <p className="text-xs font-semibold text-green-600">
                                        +{orderDetails.currency} {itemAddOnPrice.toFixed(3)}
                                      </p>
                                    ) : (
                                      <p className="text-xs text-slate-400">
                                        {isComplimentary ? "Included in Package" : "Included"}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {packageAddOnsPrice > 0 && (
                            <div className="mt-2 pt-2 border-t border-purple-200 flex justify-between text-xs font-semibold">
                              <span className="text-slate-600">Package Add-ons Total:</span>
                              <span className="text-green-600">+{orderDetails.currency} {packageAddOnsPrice.toFixed(3)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {(pkg.special_instructions_1 || pkg.special_instructions_2) && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <p className="text-xs text-slate-500 font-semibold uppercase mb-1">Special Instructions</p>
                          <p className="text-sm text-slate-700">
                            {pkg.special_instructions_1 || pkg.special_instructions_2}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Items List - Only for non-combined orders */}
          {!isCombinedOrder && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Order Items</h3>
              <div className="space-y-3">
                {orderDetails.serviceItems && orderDetails.serviceItems.length > 0 ? (
                orderDetails.serviceItems.map((item, index) => {
                  const itemAddOnPrice = parseFloat(item.add_on_price || item.menu_item?.add_on_price || 0);
                  const quantity = parseInt(item.quantity || item.choose || 1);
                  const isComplimentary = item.is_complementary === true || item.is_complementary === 1;

                  return (
                    <div key={index} className="bg-slate-50 border border-slate-200 rounded-lg p-4 hover:bg-slate-100 transition-colors">
                      <div className="flex gap-4">
                        {/* Image */}
                        {item.menu_item?.image && (
                          <div className="flex-shrink-0">
                            <img
                              src={item.menu_item.image}
                              alt={item.menu_item.name_en || item.menu_item.name_ar}
                              className="w-16 h-16 object-cover rounded-md shadow-sm"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Item Details */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold text-slate-800">
                                {item.menu_item?.name_en || item.menu_item?.name_ar || "Item"}
                                {item.add_on_type && item.add_on_type.trim() !== "" && (
                                  <span className="ml-2 text-xs font-medium text-white bg-amber-100 px-2 py-0.5 rounded capitalize">
                                    {item.add_on_type}
                                  </span>
                                )}
                                {isComplimentary && (
                                  <span className="ml-2 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded">
                                    Complimentary
                                  </span>
                                )}
                              </h4>
                              <p className="text-xs text-slate-500">
                                {item.category_name_en || item.category_name_ar || ""}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-slate-700">
                                Qty: {quantity}
                              </p>
                            </div>
                          </div>
                          
                        {/* Pricing */}
                          <div className="flex justify-between items-center text-sm">
                            <div className="text-slate-600">
                              {item.portion && (
                                <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded">
                                  Portion: {item.portion}
                                </span>
                              )}
                            </div>
                            <div className="text-right">
                            {/* Show add-on price only if NOT complimentary and price > 0 */}
                            {!isComplimentary && itemAddOnPrice > 0 && (
                                <p className="text-sm font-semibold text-green-600">
                                  Add-on: +{orderDetails.currency} {itemAddOnPrice.toFixed(3)}
                                </p>
                              )}
                            {/* Show "Included in Package" for complimentary items or free items */}
                            {(isComplimentary || itemAddOnPrice === 0) && (
                              <p className="text-sm text-slate-500">
                                {isComplimentary ? "" : "Included"}
                              </p>
                            )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-center py-4">No items found</p>
              )}
              </div>
            </div>
          )}

          {/* Notes */}
          {orderDetails.notes && orderDetails.notes.trim() !== "" && orderDetails.notes !== "N/A" && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2 tracking-wide">Special Instructions</h3>
              <p className="text-gray-700 bg-slate-50 border border-slate-200 p-3 rounded-lg">{orderDetails.notes}</p>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Price Breakdown</h3>
            <div className="space-y-2.5">
              {!isCombinedOrder ? (
                <>
                  <div className="flex justify-between text-gray-700">
                    <span>Base Package Price ({orderDetails.noOfPax} PAX)</span>
                    <span className="font-medium text-slate-800">{orderDetails.currency} {basePrice.toFixed(3)}</span>
                  </div>
                  
                  {totalAddOnsPrice > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>Add-ons</span>
                      <span className="font-medium text-green-600">+{orderDetails.currency} {totalAddOnsPrice.toFixed(3)}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {orderDetails.combineOrders && orderDetails.combineOrders.map((pkg, index) => (
                    <div key={index} className="flex justify-between text-gray-700">
                      <span className="text-sm">
                        <span className="font-semibold text-purple-700">Package {index + 1}:</span> {pkg.service_item_name_en || pkg.service_item_name_ar}
                      </span>
                      <span className="font-medium text-slate-800">{orderDetails.currency} {parseFloat(pkg.service_item_price || 0).toFixed(3)}</span>
                    </div>
                  ))}
                  
                  {totalAddOnsPrice > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>Total Add-ons</span>
                      <span className="font-medium text-green-600">+{orderDetails.currency} {totalAddOnsPrice.toFixed(3)}</span>
                    </div>
                  )}
                </>
              )}
              
              {deliveryCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Delivery Charges</span>
                  <span className="font-medium text-slate-800">+{orderDetails.currency} {deliveryCharges.toFixed(3)}</span>
                </div>
              )}
              
              {discountPrice > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Discount</span>
                  <span className="font-medium text-red-600">-{orderDetails.currency} {discountPrice.toFixed(3)}</span>
                </div>
              )}
              
              {paymentFee > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Payment Processing Fee</span>
                  <span className="font-medium text-slate-600">+{orderDetails.currency} {paymentFee.toFixed(3)}</span>
                </div>
              )}
              
              {/* Bilbayt Information */}
              {(() => {
                const isBilbayt = orderDetails.is_bilbayt === 1 || orderDetails.is_bilbayt === true || orderDetails.orderStatus === "Bilbayt";
                const bilbaytPrice = parseFloat(orderDetails.bilbayt_price || 0);
                const bilbaytDiscount = parseFloat(orderDetails.bilbayt_discount || 0);
                const bilbaytOrderId = orderDetails.bilbayt_order_id || "";
                
                if (isBilbayt && bilbaytPrice > 0) {
                  return (
                    <>
                      <div className="border-t border-amber-200 pt-3 mt-3">
                        <div className="flex justify-between text-gray-700 mb-2">
                          <span className="flex items-center gap-2 font-semibold text-amber-700">
                            <img src="/Images/bilbayt.jpeg" alt="Bilbayt" className="w-5 h-5 object-contain" />
                            Bilbayt Payment
                          </span>
                          <span className="font-medium text-amber-700">{orderDetails.currency} {bilbaytPrice.toFixed(3)}</span>
                        </div>
                        {bilbaytDiscount > 0 && (
                          <div className="flex justify-between text-gray-700 mb-2">
                            <span className="text-green-600">Bilbayt Discount</span>
                            <span className="font-medium text-green-600">-{orderDetails.currency} {bilbaytDiscount.toFixed(3)}</span>
                          </div>
                        )}
                        {bilbaytOrderId && (
                          <div className="text-xs text-gray-500 italic mt-1">
                            Bilbayt Order ID: {bilbaytOrderId}
                          </div>
                        )}
                      </div>
                    </>
                  );
                }
                return null;
              })()}
              
              <div className="border-t border-slate-300 pt-3 mt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-slate-900">Total</span>
                  <span className="text-slate-900">{orderDetails.currency} {totalPrice.toFixed(3)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 print:hidden">
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Order
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-slate-700 hover:bg-slate-800 text-white font-semibold py-3 rounded-lg transition-colors shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

CateringOrderDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderDetails: PropTypes.shape({
    orderNo: PropTypes.string,
    restaurant: PropTypes.string,
    serviceItemName: PropTypes.string,
    customerName: PropTypes.string,
    customerPhoneNumber: PropTypes.string,
    customerPhoneCountryCode: PropTypes.string,
    deliveryArea: PropTypes.string,
    orderDate: PropTypes.string,
    serviceItemDate: PropTypes.string,
    serviceItemSlot: PropTypes.string,
    noOfPax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orderStatus: PropTypes.string,
    paymentMethod: PropTypes.string,
    updatedByAgentId: PropTypes.string,
    updatedDatetime: PropTypes.string,
    serviceItems: PropTypes.array,
    notes: PropTypes.string,
    service_item_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    deliveryCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentProcessingFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Bilbayt information
    is_bilbayt: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bilbayt_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bilbayt_discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bilbayt_order_id: PropTypes.string,
    userType: PropTypes.string,
    addressDetails: PropTypes.shape({
      addressType: PropTypes.string,
      block: PropTypes.string,
      street: PropTypes.string,
      avenue: PropTypes.string,
      houseNo: PropTypes.string,
      building: PropTypes.string,
      floor: PropTypes.string,
      apartmentNo: PropTypes.string,
      companyName: PropTypes.string,
      mobileNumber: PropTypes.string,
      countryCode: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
    }),
    // Combined order fields
    isCombineOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    combineOrders: PropTypes.arrayOf(PropTypes.shape({
      order_id: PropTypes.number,
      order_no: PropTypes.string,
      service_item_id: PropTypes.number,
      service_item_name_en: PropTypes.string,
      service_item_name_ar: PropTypes.string,
      restaurant_id: PropTypes.number,
      restaurant_name_en: PropTypes.string,
      restaurant_name_ar: PropTypes.string,
      no_of_pax: PropTypes.string,
      service_item_price: PropTypes.string,
      service_item_date: PropTypes.string,
      service_item_slot: PropTypes.string,
      special_instructions_1: PropTypes.string,
      special_instructions_2: PropTypes.string,
    })),
    combineOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default CateringOrderDetailsModal;

